import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MdCode, MdDataObject, MdDynamicForm, MdFactCheck, MdSave, MdTranslate, MdVisibility } from "react-icons/md";
import DynamicJsonForm from "./DynamicJsonForm";
import HtmlConsole from "./HtmlConsole";
import TranslationsPanel from "./TranslationsPanel";
import { DeviceToggle, EmailPreviewFrame, IssueList, JsonEditor, OkLine, copyText } from "./EditorParts";
import { analyzeContentTokens, buildTranslationPackage, compareStructures, isPlainObject, languageName, listLeaves, parseJsonWithLocation, partsToToken, resolveContentToken, scalarTokenPaths, tokensIn, validateEmailHtml } from "./contentUtils";
import { kitText } from "./kitText";
import "./emailContentKit.css";

// Same rule as the server's send gate (findUnsafeEmailUrls).
const UNREACHABLE_URL = /^(blob:|file:|https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$))/i;

// ONE email = ONE underlying content (a JSON document per language) + an
// optional HTML template. Form, JSON, HTML, Preview, Translations and
// Validation are views of that same state: a form edit rewrites the JSON, a
// valid JSON edit refreshes the form, an invalid JSON keeps the last valid
// document in use, and nothing is stored until an explicit Save.
//
// Props
//   languages / sourceLanguage       enabled language codes, source of the structure
//   documents                        saved { [language]: object }
//   onSaveDocument(language, doc)    async -> saved documents (all languages) or throws
//   html / originalHtml / onSaveHtml optional template (HTML tab when onSaveHtml is given)
//   referenceHtml / referenceTokens  read-only template (or its token names) used for token checks (no HTML tab)
//   readOnly                         view only (no save)
//   registry                         { types, protected, contentPrefix, contentPaths }
//   tokenSyntax                      "plain" ({{hero.title}}) | "content" ({{content.hero.title}})
//   lockedPaths                      built-in keys (editable, never removable)
//   defaults / labelFor              built-in default texts per language, friendly field labels
//   renderPreview({language, document, html}) async -> { html, subject }
//   previewControls / previewKey     extra preview selectors and their state key
//   settings                         Email Marketing "Content & Languages" settings
//   onDirtyChange(boolean)
const initialDrafts = (languages, documents) => Object.fromEntries(languages.map((code) => {
  const value = isPlainObject(documents?.[code]) ? documents[code] : {};
  return [code, { text: JSON.stringify(value, null, 2), value, error: null }];
}));

export default function EmailContentEditor({
  languages, sourceLanguage = "en", documents, onSaveDocument, html, originalHtml, onSaveHtml, referenceHtml,
  registry = {}, tokenSyntax = "plain", lockedPaths, defaults, labelFor, renderPreview, previewControls, previewKey, settings,
  initialLanguage, initialTab, onDirtyChange, emailType = "email", languageLabel, referenceTokens, readOnly = false, keyPattern,
}) {
  const t = kitText();
  const [saved, setSaved] = useState(() => Object.fromEntries(languages.map((code) => [code, isPlainObject(documents?.[code]) ? documents[code] : {}])));
  const [drafts, setDrafts] = useState(() => initialDrafts(languages, documents));
  const [language, setLanguage] = useState(initialLanguage && languages.includes(initialLanguage) ? initialLanguage : sourceLanguage);
  const hasHtmlTab = typeof onSaveHtml === "function";
  const [tab, setTab] = useState(initialTab || "form");
  const [savedHtml, setSavedHtml] = useState(html || "");
  const [htmlDraft, setHtmlDraft] = useState(html || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState("");
  const [device, setDevice] = useState(settings?.preview?.device || "desktop");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const label = useCallback((code) => languageLabel?.(code) || languageName(code), [languageLabel]);

  const draft = drafts[language];
  const isDirty = (code) => Boolean(drafts[code]?.error) || JSON.stringify(drafts[code]?.value) !== JSON.stringify(saved[code]);
  const htmlDirty = hasHtmlTab && htmlDraft !== savedHtml;
  const anyDirty = languages.some(isDirty) || htmlDirty;
  useEffect(() => { onDirtyChange?.(anyDirty); }, [anyDirty, onDirtyChange]);
  // Protects a reload / tab close; switching language or tab keeps the
  // drafts in memory and never asks.
  useEffect(() => {
    if (!anyDirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [anyDirty]);
  useEffect(() => { if (!notice) return undefined; const timer = setTimeout(() => setNotice(""), 3500); return () => clearTimeout(timer); }, [notice]);

  const setDocument = useCallback((code, value) => setDrafts((d) => ({ ...d, [code]: { text: JSON.stringify(value, null, 2), value, error: null } })), []);
  const setText = (code, text) => setDrafts((d) => {
    const parsed = parseJsonWithLocation(text);
    if (parsed.ok && !isPlainObject(parsed.value)) return { ...d, [code]: { ...d[code], text, error: { message: t.rootMustBeObject } } };
    return { ...d, [code]: parsed.ok ? { text, value: parsed.value, error: null } : { ...d[code], text, error: parsed.error } };
  });

  // Content keys of every language are valid tokens (new keys included).
  const liveRegistry = useMemo(() => {
    const paths = new Set(registry.contentPaths || []);
    languages.forEach((code) => scalarTokenPaths(drafts[code]?.value).forEach((path) => paths.add(path)));
    return { ...registry, contentPrefix: tokenSyntax === "content" ? "content." : registry.contentPrefix, contentPaths: [...paths] };
  }, [registry, languages, drafts, tokenSyntax]);
  // The token of a JSON value is its full path; null when a key cannot be
  // written in a token (the field stays editable, with a warning).
  const tokenFor = useCallback((parts) => { const token = partsToToken(parts); return token ? `{{${tokenSyntax === "content" ? "content." : ""}${token}}}` : null; }, [tokenSyntax]);
  const lockedSet = useMemo(() => new Set(lockedPaths || []), [lockedPaths]);

  const saveDocument = async () => {
    if (draft.error) { setError({ message: t.fixJsonFirst }); return; }
    // "Translation structure check" (settings): a language whose keys differ
    // from the source is saved only after an explicit confirmation.
    if (settings?.validation?.json !== false && language !== sourceLanguage) {
      const diff = compareStructures(drafts[sourceLanguage]?.value || {}, draft.value);
      const keys = [...diff.missing.map((path) => `-${path}`), ...diff.extra.map((path) => `+${path}`), ...diff.typeMismatches.map((m) => `~${m.path}`)];
      if (keys.length && !window.confirm(t.saveStructureWarning.replace("{language}", label(language)).replace("{count}", keys.length).replace("{keys}", keys.slice(0, 12).join(", ")))) return;
    }
    setSaving(true); setError(null);
    try {
      const result = await onSaveDocument(language, draft.value);
      const next = result?.documents || { ...saved, [language]: draft.value };
      setSaved((s) => ({ ...s, ...Object.fromEntries(languages.map((code) => [code, isPlainObject(next[code]) ? next[code] : s[code]])) }));
      if (isPlainObject(next[language])) setDocument(language, next[language]);
      setNotice(t.savedLanguage.replace("{language}", label(language)));
      if (result?.warnings?.length) setError({ message: t.savedWithWarnings, warnings: result.warnings });
    } catch (e) {
      setError({ message: e.message, details: e.payload?.details || e.details });
    } finally { setSaving(false); }
  };
  const saveHtml = async (value) => {
    setSaving(true); setError(null);
    try {
      const result = await onSaveHtml(value);
      const stored = typeof result?.html === "string" ? result.html : value;
      setSavedHtml(stored); setHtmlDraft(stored);
      setNotice(t.htmlSaved);
      return true;
    } catch (e) {
      setError({ message: e.message, details: e.payload?.details || e.details });
      return false;
    } finally { setSaving(false); }
  };

  // Live preview of the drafts (debounced); sample data only, nothing saved.
  const previewDoc = draft?.value;
  const previewHtml = hasHtmlTab ? htmlDraft : undefined;
  useEffect(() => {
    if (tab !== "preview" || !renderPreview) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      renderPreview({ language, document: previewDoc, html: previewHtml })
        .then((value) => { if (active) { setPreview(value); setPreviewError(""); } })
        .catch((e) => { if (active) setPreviewError(e.message); });
    }, 400);
    return () => { active = false; clearTimeout(timer); };
  }, [tab, language, previewDoc, previewHtml, renderPreview, previewKey]);

  // Validation tab: every language against the source, the template, and
  // the content tokens the template uses that a language lacks.
  // Content tokens of the template (HTML tab, reference HTML, or the token
  // names the server reported) against the JSON of every language.
  const isContentToken = useCallback((name) => (liveRegistry.open
    ? !liveRegistry.isReserved?.(name)
    : !(liveRegistry.types && Object.prototype.hasOwnProperty.call(liveRegistry.types, name)) && !(liveRegistry.protected || []).includes(name)), [liveRegistry]);
  const validation = useMemo(() => {
    if (tab !== "validation") return null;
    const templateHtml = hasHtmlTab ? htmlDraft : referenceHtml;
    const names = templateHtml ? tokensIn(templateHtml) : (referenceTokens || []);
    const htmlCheck = templateHtml ? validateEmailHtml(templateHtml, { registry: liveRegistry, unknownTokens: settings?.validation?.unknownTokens || "warn", checkStructure: settings?.validation?.html !== false }) : null;
    const source = drafts[sourceLanguage]?.value || {};
    const analysis = analyzeContentTokens(names, source, isContentToken);
    const contentNames = [...new Set(names.filter(isContentToken))];
    const systemTokens = [...new Set(names.filter((name) => !isContentToken(name)))];
    const perLanguage = languages.map((code) => {
      const d = drafts[code];
      const structure = code === sourceLanguage || d.error ? null : compareStructures(source, d.value);
      const missingTokens = d.error ? [] : contentNames.filter((name) => !resolveContentToken(d.value, name).found);
      return { code, error: d.error, structure, missingTokens, empty: !Object.keys(d.value || {}).length };
    });
    // Same rule as the server's send gate (findUnsafeEmailUrls): a recipient
    // can never open a localhost / blob: / file: URL.
    const unreachable = languages.flatMap((code) => (drafts[code]?.error ? [] : listLeaves(drafts[code]?.value || {})
      .filter((leaf) => typeof leaf.value === "string" && UNREACHABLE_URL.test(leaf.value.trim()))
      .map((leaf) => `${label(code)} · ${leaf.path}: ${leaf.value}`)));
    return { htmlCheck, analysis, systemTokens, unreachable, hasTemplate: Boolean(templateHtml || (referenceTokens || []).length), perLanguage, protectedMissing: (liveRegistry.protected || []).filter((name) => names.length && !names.includes(name)) };
  }, [tab, hasHtmlTab, htmlDraft, referenceHtml, referenceTokens, liveRegistry, settings, languages, drafts, sourceLanguage, isContentToken, label]);

  const tabs = [
    ["form", t.formTab, MdDynamicForm],
    ["json", t.jsonTab, MdDataObject],
    ...(hasHtmlTab ? [["html", t.htmlTab, MdCode]] : []),
    ["preview", t.previewTab, MdVisibility],
    ["translations", t.translationsTab, MdTranslate],
    ["validation", t.validationTab, MdFactCheck],
  ];
  const languageTab = ["form", "json", "preview"].includes(tab);

  return <div className="eck">
    <div className="eck-head">
      <div className="eck-langs" role="group" aria-label={t.language}>
        {languages.map((code) => <button key={code} type="button" aria-pressed={language === code} className={`eck-lang${language === code ? " is-active" : ""}`} onClick={() => setLanguage(code)}>
          {label(code)}{code === sourceLanguage && <small> · {t.source}</small>}
          {isDirty(code) && <span className="eck-dot" aria-label={t.unsaved} title={t.unsaved} />}
        </button>)}
      </div>
      <span className="eck-toolbar__spacer" />
      {anyDirty ? <span className="eck-pill is-warn">{t.unsaved}</span> : <span className="eck-pill is-ok">{t.allSaved}</span>}
      {languageTab && !readOnly && <button type="button" className="eck-btn eck-btn--primary" disabled={saving || !isDirty(language) || Boolean(draft?.error)} onClick={saveDocument}><MdSave aria-hidden="true" /> {saving ? t.saving : t.saveLanguage.replace("{language}", label(language))}</button>}
    </div>

    <div className="eck-tabs" role="tablist">
      {tabs.map(([key, text, Icon]) => <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}>
        <Icon aria-hidden="true" /> {text}
        {key === "html" && htmlDirty && <span className="eck-dot" />}
      </button>)}
    </div>

    {notice && <div className="eck-notice" role="status">{notice}</div>}
    {error && <div className="eck-alert" role="alert">
      <strong>{error.message}</strong>
      {error.details && <IssueList errors={error.details.errors} warnings={error.details.warnings} t={t} />}
      {error.warnings && <IssueList warnings={error.warnings} t={t} />}
    </div>}

    {tab === "form" && <div className="eck-pane">
      {draft.error && <div className="eck-alert">{t.formShowsLastValid}</div>}
      <input className="eck-input eck-search" type="search" aria-label={t.searchFields} placeholder={t.searchFields} value={filter} onChange={(e) => setFilter(e.target.value)} />
      <DynamicJsonForm value={draft.value} onChange={(value) => setDocument(language, value)} t={t} lockedPaths={lockedSet}
        sourceDocument={language === sourceLanguage ? null : drafts[sourceLanguage]?.value} defaultDocument={defaults?.[language]} labelFor={labelFor} tokenFor={tokenFor} filter={filter}
        onTokenClick={(token) => copyText(token).then(() => setNotice(t.tokenCopied.replace("{token}", token)))} keyPattern={keyPattern} />
    </div>}

    {tab === "json" && <div className="eck-pane">
      <JsonEditor t={t} text={draft.text} error={draft.error} onChange={(text) => setText(language, text)}
        onFormat={() => setDocument(language, draft.value)} canReset={isDirty(language)}
        onReset={() => { if (window.confirm(t.confirmResetJson.replace("{language}", label(language)))) setDocument(language, saved[language]); }}
        onCopy={() => copyText(draft.text).then(() => setNotice(t.copied))} label={`${t.jsonTab} — ${label(language)}`}
        onCopyForTranslation={() => copyText(buildTranslationPackage({ document: draft.value, sourceLanguage: language, targetLanguage: null, customInstructions: settings?.ai?.customInstructions })).then(() => setNotice(t.promptCopied))} />
    </div>}

    {tab === "html" && hasHtmlTab && <div className="eck-pane">
      <HtmlConsole html={htmlDraft} savedHtml={savedHtml} originalHtml={originalHtml} onChange={setHtmlDraft} onSave={saveHtml} saving={saving}
        registry={liveRegistry} t={t} document={drafts[sourceLanguage]?.value} onApplyDocument={(doc) => setDocument(sourceLanguage, doc)}
        tokenSyntax={tokenSyntax} emailType={emailType} settings={settings} notify={setNotice} />
    </div>}

    {tab === "preview" && <div className="eck-pane">
      <h5 className="eck-preview-title">{t.previewOf.replace("{language}", label(language))}</h5>
      <div className="eck-toolbar">
        {previewControls}
        <span className="eck-toolbar__spacer" />
        <DeviceToggle device={device} setDevice={setDevice} t={t} />
      </div>
      {previewError && <div className="eck-alert" role="alert">{previewError}</div>}
      {preview?.fallbackUsed && preview.usedLanguage && <div className="eck-notice eck-notice--info">{t.previewFallback.replace("{language}", label(language)).replace("{fallback}", label(preview.usedLanguage))}</div>}
      {preview?.subject && <div className="eck-subject"><span>{t.subject}</span> <strong>{preview.subject}</strong></div>}
      {preview && <EmailPreviewFrame html={preview.html} device={device} title={`${t.previewTab} — ${label(language)}`} />}
      <p className="eck-hint">{t.previewSampleNote}{(anyDirty) ? ` ${t.previewDraftNote}` : ""}</p>
    </div>}

    {tab === "translations" && <div className="eck-pane">
      <TranslationsPanel languages={languages} sourceLanguage={sourceLanguage} drafts={drafts} setDocument={setDocument} t={t} settings={settings} notify={setNotice} languageLabel={label} />
      <p className="eck-hint">{t.translationsSaveHint}</p>
    </div>}

    {tab === "validation" && validation && <div className="eck-pane">
      <section className="eck-panel">
        <h5>{t.summaryTitle}</h5>
        <ul className="eck-summary">
          <li>{validation.perLanguage.some((entry) => entry.error) ? <span className="eck-field__error">✗ {t.jsonInvalidSome}</span> : <span className="eck-ok-inline">✓ {t.jsonValid}</span>}</li>
          <li>{validation.hasTemplate ? <span className="eck-ok-inline">✓ {t.htmlAvailable}</span> : <span className="eck-hint">{t.noTemplateTokens}</span>}</li>
          <li><strong>{t.contentTokensResolved.replace("{resolved}", validation.analysis.resolved).replace("{total}", validation.analysis.contentTokens)}</strong>
            {validation.analysis.missing.length > 0 && <div className="eck-keylist is-error"><span>{t.missingFromJson.replace("{count}", validation.analysis.missing.length)}</span> {validation.analysis.missing.map((name) => <code key={name}>{`{{${name}}}`}</code>)}</div>}</li>
          <li>{t.systemTokens}: {validation.systemTokens.length ? validation.systemTokens.map((name) => <code key={name} className="eck-inline-code">{`{{${name}}}`}</code>) : "—"}
            {validation.protectedMissing.length > 0 && <div className="eck-field__error">{t.missingProtectedList} {validation.protectedMissing.map((name) => `{{${name}}}`).join(", ")}</div>}</li>
          <li>{t.unusedJson.replace("{count}", validation.analysis.unusedLeaves.length)} <span className="eck-hint">{t.unusedJsonHint}</span>
            {validation.analysis.unusedLeaves.length > 0 && <details><summary className="eck-hint">{t.showList}</summary><div className="eck-keylist">{validation.analysis.unusedLeaves.map((path) => <code key={path}>{path}</code>)}</div></details>}</li>
          {validation.unreachable.length > 0 && <li className="eck-field__error">{t.unreachableUrls} <div className="eck-keylist is-error">{validation.unreachable.map((entry) => <code key={entry}>{entry}</code>)}</div></li>}
          {validation.analysis.untokenizableKeys.length > 0 && <li className="eck-field__warning">{t.untokenizableList} {validation.analysis.untokenizableKeys.join(", ")}</li>}
        </ul>
      </section>
      {validation.htmlCheck && <section className="eck-panel">
        <h5>{t.templateValidation}</h5>
        {validation.htmlCheck.valid && !validation.htmlCheck.warnings.length && <OkLine>{t.htmlValid}</OkLine>}
        <IssueList errors={validation.htmlCheck.errors} warnings={validation.htmlCheck.warnings} t={t} />
      </section>}
      <section className="eck-panel">
        <h5>{t.languagesValidation}</h5>
        <ul className="eck-lang-report">
          {validation.perLanguage.map((entry) => <li key={entry.code}>
            <strong>{label(entry.code)}</strong>
            {entry.error ? <span className="eck-field__error">{entry.error.message}</span>
              : <>
                {entry.code === sourceLanguage && <span className="eck-pill is-ok">{t.source}</span>}
                {entry.empty && <span className="eck-pill is-warn">{t.noContent}</span>}
                {entry.structure && (entry.structure.inSync ? <span className="eck-pill is-ok">{t.complete}</span>
                  : <span className="eck-pill is-warn">{t.structureSummary.replace("{missing}", entry.structure.missing.length).replace("{extra}", entry.structure.extra.length).replace("{types}", entry.structure.typeMismatches.length)}</span>)}
                {entry.missingTokens.length > 0 && <span className="eck-hint">{t.tokensMissingInLanguage} {entry.missingTokens.length > 8 ? `${entry.missingTokens.slice(0, 8).join(", ")} … (+${entry.missingTokens.length - 8})` : entry.missingTokens.join(", ")}</span>}
              </>}
          </li>)}
        </ul>
      </section>
    </div>}
  </div>;
}
